import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Loader2, CheckCircle2, AlertCircle, Mail } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CADENCE_OPTIONS = [
  {
    value: 'daily',
    label: 'Daily firehose',
    detail: 'Mon / Tue / Thu / Fri — the latest AI news + tools. Wed founder note. ~5 emails per week.',
  },
  {
    value: 'founder_only',
    label: 'Wednesday founder note only',
    detail: 'Just Jake\'s weekly note on what shipped, what\'s coming, and what he\'s noticed. 1 email per week.',
  },
  {
    value: 'weekly',
    label: 'Friday roll-up only',
    detail: 'One email on Friday with the week\'s biggest AI news + tools we\'re featuring. 1 email per week.',
  },
];

export default function NewsletterPreferences() {
  const [sp] = useSearchParams();
  const token = sp.get('token') || '';

  const [state, setState] = useState({ loading: true, error: '', prefs: null });
  const [cadence, setCadence] = useState('daily');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!token) {
      setState({ loading: false, error: 'This link is missing its access token.', prefs: null });
      return;
    }
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/api/newsletter/preferences`, { params: { token } });
        setState({ loading: false, error: '', prefs: res.data });
        setCadence(res.data.cadence || 'daily');
      } catch (err) {
        setState({
          loading: false,
          error: err.response?.data?.detail || 'Could not load your preferences.',
          prefs: null,
        });
      }
    })();
  }, [token]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/newsletter/preferences`, { token, cadence });
      toast.success('Preferences saved. New settings apply to the next send.');
      setState((s) => ({ ...s, prefs: { ...s.prefs, cadence } }));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not save preferences.');
    } finally {
      setSaving(false);
    }
  };

  if (state.loading) {
    return (
      <div className="min-h-screen bg-neutral-950 text-white flex items-center justify-center p-6" data-testid="prefs-loading">
        <Loader2 className="w-6 h-6 animate-spin text-[#00F0FF]" />
      </div>
    );
  }

  if (state.error) {
    return (
      <div className="min-h-screen bg-neutral-950 text-white flex items-center justify-center p-6">
        <div className="max-w-md text-center space-y-3" data-testid="prefs-error">
          <AlertCircle className="w-10 h-10 text-red-400 mx-auto" />
          <h1 className="text-2xl font-semibold">Link no longer works</h1>
          <p className="text-neutral-400 text-sm">{state.error}</p>
          <p className="text-neutral-500 text-xs">
            If you want to update your preferences, use the &quot;Manage email preferences&quot; link inside your most recent email from us.
          </p>
        </div>
      </div>
    );
  }

  const unchanged = cadence === (state.prefs?.cadence || 'daily');

  return (
    <div className="min-h-screen bg-neutral-950 text-white flex items-center justify-center p-6">
      <div className="max-w-xl w-full bg-neutral-900 border border-neutral-800 rounded-2xl p-8 space-y-6" data-testid="prefs-panel">
        <div className="flex items-start gap-3">
          <Mail className="w-6 h-6 text-[#00F0FF] shrink-0 mt-1" />
          <div>
            <h1 className="text-2xl font-semibold">Your email preferences</h1>
            <p className="text-neutral-400 text-sm mt-1">
              Signed in as <span className="text-white">{state.prefs.email}</span>.
              Pick how often you want to hear from The AI Ledger.
            </p>
          </div>
        </div>

        <div className="space-y-3">
          {CADENCE_OPTIONS.map((opt) => (
            <label
              key={opt.value}
              className={`block border rounded-xl p-4 cursor-pointer transition ${
                cadence === opt.value
                  ? 'border-[#00F0FF] bg-[#00F0FF]/5'
                  : 'border-neutral-800 hover:border-neutral-700'
              }`}
              data-testid={`cadence-option-${opt.value}`}
            >
              <div className="flex items-start gap-3">
                <input
                  type="radio"
                  name="cadence"
                  value={opt.value}
                  checked={cadence === opt.value}
                  onChange={() => setCadence(opt.value)}
                  className="mt-1 accent-[#00F0FF]"
                />
                <div className="flex-1">
                  <div className="font-medium text-white flex items-center gap-2">
                    {opt.label}
                    {state.prefs.cadence === opt.value && (
                      <span className="text-[10px] uppercase tracking-wider text-[#00F0FF] flex items-center gap-1">
                        <CheckCircle2 className="w-3 h-3" /> current
                      </span>
                    )}
                  </div>
                  <div className="text-sm text-neutral-400 mt-1">{opt.detail}</div>
                </div>
              </div>
            </label>
          ))}
        </div>

        <button
          type="button"
          onClick={handleSave}
          disabled={saving || unchanged}
          className={`w-full py-3 rounded-xl font-semibold text-sm transition ${
            unchanged || saving
              ? 'bg-neutral-800 text-neutral-500 cursor-not-allowed'
              : 'bg-[#00F0FF] text-black hover:bg-[#00F0FF]/80'
          }`}
          data-testid="prefs-save-btn"
        >
          {saving ? 'Saving…' : unchanged ? 'No changes to save' : 'Save preferences'}
        </button>

        <p className="text-xs text-neutral-500 text-center">
          Want out entirely? Use the &quot;One-click unsubscribe&quot; link in any email.
        </p>
      </div>
    </div>
  );
}
